import React from 'react'
import { Toaster } from 'sonner'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect, useMemo, useRef, Suspense } from 'react'
import { supabase } from './lib/supabase'
import { shouldShowLoadingScreen, debugPWAStatus } from './utils/pwaHelpers'
import { useAuth } from './contexts/AuthContext'
import { Capacitor } from '@capacitor/core'

// Components
import Login from './features/auth/components/LoginPage'
import ResetPassword from './features/auth/components/ResetPasswordPage'
import ExternalSignup from './features/auth/components/ExternalSignupPage'
import SignupInfo from './features/auth/components/SignupInfoPage'
// Route-split heavy dashboards — chunk-error recovery via shared helper
import { lazyWithRetry, ChunkGuardBoundary } from './utils/chunkErrorRecovery'
const TeacherDashboard = lazyWithRetry(() => import('./features/teacherDashboard/components/TeacherDashboardPage'), 'teacher')
const ParentPortalV2 = lazyWithRetry(() => import('./features/parent-portal/components/ParentPortalPage'), 'parent')
const AdminDashboard = lazyWithRetry(() => import('./features/admin-dashboard/components/AdminDashboardPage'), 'admin')
const VertretungsplanungDashboard = lazyWithRetry(() => import('./features/substitute-planning/components/VertretungsplanungRoutePage'), 'vertretungsplanung')
const VertretungsplanungLayoutPage = lazyWithRetry(() => import('./features/substitute-planning/layout-view/VertretungsplanLayoutPage'), 'vertretungsplanung-layout')
const PlanningOverviewPage = lazyWithRetry(() => import('./features/substitute-planning/planning-overview'), 'planning-overview')
const StatisticsPage = lazyWithRetry(() => import('./features/substitute-planning/statistics'), 'substitution-statistics')
const CourseAttendanceReportPage = lazyWithRetry(() => import('./features/course-management/attendance-report'), 'course-attendance-report')
const RlsInspectorPage = lazyWithRetry(() => import('./features/security-management/components/RlsInspector'), 'rls-inspector')
// Fehlzeiten feature routes
const FehlzeitenLayout = lazyWithRetry(() => import('./features/absence-tracking/FehlzeitenLayout'), 'fehlzeiten')
const FehlzeitDetailRoute = lazyWithRetry(() => import('./features/absence-tracking/shared/components/FehlzeitDetailRoute'), 'fehlzeit-detail')
// Wünsche feature route
const WuenscheRoutePage = lazyWithRetry(() => import('./features/wuensche/shared/components/WuenscheRoutePage'), 'wuensche')
// Klassenbuch feature route
const KlassenbuchRoutePage = lazyWithRetry(() => import('./features/klassenbuch/shared/components/KlassenbuchRoutePage'), 'klassenbuch')
const PruefungenRoutePage = lazyWithRetry(() => import('./features/pruefungen/shared/components/PruefungenRoutePage'), 'pruefungen')
// Bänder statistics route
const BaendeStatisticsRoutePage = lazyWithRetry(() => import('./features/statistics/baende/components/BaendeStatisticsRoutePage'), 'baende-statistik')
// Klassenübersicht feature route
const KlassenuebersichtRoutePage = lazyWithRetry(() => import('./features/klassenuebersicht/KlassenuebersichtRoutePage'), 'klassenuebersicht')
// Aufsichten feature route
const AufsichtenPage = lazyWithRetry(() => import('./features/aufsichten'), 'aufsichten')
// Flex Übersicht feature route
const FlexbandRoutePage = lazyWithRetry(() => import('./features/flexband/components/FlexbandRoutePage'), 'flex-uebersicht')
// FlexModule Vorschläge feature route
const FlexModuleProposalRoutePage = lazyWithRetry(() => import('./features/flex-module-vorschlaege/shared/components/FlexModuleProposalRoutePage'), 'flexmodule-vorschlaege')
// Newsletter feature routes
const NewsletterRoutePage = lazyWithRetry(() => import('./features/newsletter/admin/components/NewsletterAdminRoutePage'), 'newsletter')
const NewsletterTeacherRoutePage = lazyWithRetry(() => import('./features/newsletter/teacher-upload/components/NewsletterTeacherRoutePage'), 'newsletter-teacher')
const NewsletterSchoolPreviewRoute = lazyWithRetry(() => import('./features/newsletter/teacher-upload/components/NewsletterSchoolPreviewRoute'), 'newsletter-school-preview')
const NewsletterPublicRoute = lazyWithRetry(() => import('./features/newsletter/public/components/NewsletterPublicRoute'), 'newsletter-public')
// Stundenplan-Planung (Stecken) feature route
const StundenplanPlanungPage = lazyWithRetry(() => import('./features/scheduling/components/StundenplanPlanungRoutePage'), 'stundenplan-planung')
const VerteilungRoutePage = lazyWithRetry(() => import('./features/scheduling/verteilungsplan/components/VerteilungRoutePage'), 'verteilung')
const TeacherUmfrageRoutePage = lazyWithRetry(() => import('./features/teacher-umfrage/form/components/TeacherUmfrageRoutePage'), 'teacher-umfrage')
const AdminUmfrageRoutePage = lazyWithRetry(() => import('./features/teacher-umfrage/admin/components/AdminUmfrageRoutePage'), 'admin-umfrage')
const SettingsRoutePage = lazyWithRetry(() => import('./features/settings/components/SettingsRoutePage'), 'einstellungen')
const HilfeRoutePage = lazyWithRetry(() => import('./features/help/components/HilfeRoutePage'), 'hilfe')
import LoadingScreen from './components/LoadingScreen'
import { MFAGuard } from './features/auth/components/MFAGuard'
import SupabaseDebug from './components/SupabaseDebug'
import Impressum, { ImpressumContent } from './app/static/impressum'
import Datenschutz, { DatenschutzContent } from './app/static/datenschutz'
import CookieBanner from './components/CookieBanner'
import Modal from './components/Modal'
import { ConnectivityProvider } from './contexts/ConnectivityContext'
import UpdateBanner from './components/UpdateBanner'
import OfflineBanner from './components/OfflineBanner'
import { SchoolRealtimeProvider } from './contexts/SchoolRealtimeContext'
import KioskPairPage from './app/kiosk/pair'
import KioskSetupPage from './app/kiosk/setup'
import KioskSubstitutionDisplayPage from './app/kiosk/display-substitution'
import KioskFlexBandDisplayPage from './app/kiosk/display-flexband'
import KioskChipTestPage from './app/kiosk/chip-test'
import KioskKlassenbuchPage from './app/kiosk/klassenbuch'
const CalendarRouteWrapper = lazyWithRetry(() => import('./features/calendar/components/CalendarRouteWrapper'), 'calendar', 'CalendarRouteWrapper')
const PublicCalendarRoute = lazyWithRetry(() => import('./features/calendar/components/PublicCalendarRoute'), 'public-calendar', 'PublicCalendarRoute')
const PublicShowcaseRoute = lazyWithRetry(() => import('./features/flex-module-vorschlaege/showcase/components/PublicShowcaseRoute'), 'public-showcase', 'PublicShowcaseRoute')
// Debug system
import { DebugProvider, DebugModal } from './debug'
import { useDebug } from './debug/DebugProvider'
import { AuthDebug } from './debug/AuthDebug'
import { TooltipProvider } from './components/ui/tooltip'

// Compatibility shim for share URLs that used to embed the token as a path
// segment. New code emits fragment-form URLs; legacy QR codes / iframes
// printed before that change land here and get redirected so they keep
// working. See lib/usePublicShareToken.ts and the README cleanup notes.
function LegacyTokenRedirect({ to }) {
  const { legacyToken } = useParams()
  if (!legacyToken) return <Navigate to={to} replace />
  return <Navigate to={`${to}#${encodeURIComponent(legacyToken)}`} replace />
}


// Renders AuthDebug only when DebugProvider marks debug mode on
function AuthDebugGate() {
  try {
    const { isDebugMode } = useDebug();
    return isDebugMode ? <AuthDebug /> : null;
  } catch {
    return null;
  }
}

// Constants
const ROLE_ROUTES = {
  'Admin': '/dashboard/admin',
  'Super Admin': '/dashboard/admin',
  'Teacher': '/dashboard/teacher',
  'Erzieher*innen': '/dashboard/teacher',
  'Parent': '/dashboard/parent',
  'Externe': '/dashboard/teacher',
  'Student': '/dashboard/parent'
}

const DEFAULT_ROUTE = '/error/missing-role'

function UnauthedRoutes() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        <Route path="/auth/login" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/external-signup" element={<ExternalSignup />} />
        <Route path="/auth/signup-info" element={<SignupInfo />} />
        <Route path="/debug/supabase" element={<SupabaseDebug />} />
        <Route path="/kiosk/setup" element={<KioskSetupPage />} />
        <Route path="/kiosk/display/substitution" element={<KioskSubstitutionDisplayPage />} />
        <Route path="/kiosk/display/flexband" element={<KioskFlexBandDisplayPage />} />
        <Route path="/kiosk/chip-test" element={<KioskChipTestPage />} />
        <Route path="/kiosk/klassenbuch" element={<KioskKlassenbuchPage />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/public/calendar" element={
          <Suspense fallback={
            <div className="bg-background min-h-screen p-4 flex flex-col items-center">
              <div className="w-full max-w-6xl flex-1 flex flex-col bg-card rounded-xl border border-border shadow-sm p-4 flex items-center justify-center">
                <p className="text-muted-foreground animate-pulse font-medium">Lade Kalender...</p>
              </div>
            </div>
          }>
            <PublicCalendarRoute />
          </Suspense>
        } />
        <Route path="/public/flexmodule-schaufenster" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <p className="text-muted-foreground animate-pulse font-medium">Lade Schaufenster…</p>
            </div>
          }>
            <PublicShowcaseRoute />
          </Suspense>
        } />
        {/* Token lives in the URL fragment (`/public/newsletter#<token>`)
            so it never reaches Netlify access logs / CDN traces / 3rd-party
            Referer headers. NewsletterPublicRoute reads it via
            usePublicShareToken(). */}
        <Route path="/public/newsletter" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <p className="text-muted-foreground animate-pulse font-medium">Lade Newsletter…</p>
            </div>
          }>
            <NewsletterPublicRoute />
          </Suspense>
        } />
        <Route path="/public/newsletter/print" element={
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center">
              <p className="text-muted-foreground animate-pulse font-medium">Lade Newsletter (Druckansicht)…</p>
            </div>
          }>
            <NewsletterPublicRoute forcePrint />
          </Suspense>
        } />
        {/* Legacy compatibility — path-form URLs printed before fragment URLs
            shipped get redirected so old QR codes / iframes keep working. */}
        <Route path="/public/newsletter/:legacyToken" element={<LegacyTokenRedirect to="/public/newsletter" />} />
        <Route path="/public/newsletter/:legacyToken/print" element={<LegacyTokenRedirect to="/public/newsletter/print" />} />
        <Route path="/public/flexmodule-schaufenster/:legacyToken" element={<LegacyTokenRedirect to="/public/flexmodule-schaufenster" />} />
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Routes>

      {state?.backgroundLocation && (
        <Routes>
          <Route
            path="/impressum"
            element={
              <Modal title="Impressum" onClose={() => navigate(-1)}>
                <ImpressumContent showHeading={false} />
              </Modal>
            }
          />
          <Route
            path="/datenschutz"
            element={
              <Modal title="Datenschutz" onClose={() => navigate(-1)}>
                <DatenschutzContent showHeading={false} />
              </Modal>
            }
          />
        </Routes>
      )}
    </>
  )
}

function GlobalLegalModals() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state
  if (!state?.backgroundLocation) return null
  return (
    <Routes>
      <Route
        path="/impressum"
        element={
          <Modal title="Impressum" onClose={() => navigate(-1)}>
            <ImpressumContent showHeading={false} />
          </Modal>
        }
      />
      <Route
        path="/datenschutz"
        element={
          <Modal title="Datenschutz" onClose={() => navigate(-1)}>
            <DatenschutzContent showHeading={false} />
          </Modal>
        }
      />
    </Routes>
  )
}

function RedirectWithLog({ to, reason }) {
  try {
    const S = require('@sentry/react')
    S.addBreadcrumb({ category: 'navigation', level: 'info', message: `redirect to ${to}: ${reason}` })
  } catch { }
  return <Navigate to={to} replace />
}

function RouteDocumentTitle() {
  const location = useLocation()

  useEffect(() => {
    const path = location.pathname
    const routeTitles = [
      { prefix: '/dashboard/kalender', title: 'Kalender' },
      { prefix: '/dashboard/klassenbuch', title: 'Klassenbuch' },
      { prefix: '/dashboard/klassenuebersicht', title: 'Klassenübersicht' },
      { prefix: '/dashboard/admin/kursuebersicht', title: 'Kursübersicht' },
      { prefix: '/dashboard/fehlzeiten', title: 'Fehlzeiten' },
      { prefix: '/dashboard/vertretungsplanung/layout', title: 'Vertretungsplanung Layout' },
      { prefix: '/dashboard/vertretungsplanung', title: 'Vertretungsplanung' },
      { prefix: '/dashboard/stundenplan-planung', title: 'Stundenplan Planung' },
      { prefix: '/dashboard/admin/umfrage', title: 'Abfrage: Stundenplanung · Verwaltung' },
      { prefix: '/dashboard/umfrage', title: 'Abfrage: Stundenplanung' },
      { prefix: '/dashboard/flex-uebersicht', title: 'Flex Übersicht' },
      { prefix: '/dashboard/flexmodule-vorschlaege', title: 'Flexmodule Vorschläge' },
      { prefix: '/dashboard/newsletter/meine-fotos', title: 'Newsletter — Meine Fotos' },
      { prefix: '/dashboard/newsletter', title: 'Newsletter' },
      { prefix: '/public/newsletter', title: 'Newsletter' },
      { prefix: '/dashboard/aufsichten', title: 'Aufsichten' },
      { prefix: '/dashboard/wuensche', title: 'Wünsche' },
      { prefix: '/dashboard/pruefungen', title: 'Prüfungen' },
      { prefix: '/dashboard/einstellungen', title: 'Einstellungen' },
      { prefix: '/dashboard/hilfe', title: 'Hilfe' },
      { prefix: '/dashboard/admin', title: 'Admin Dashboard' },
      { prefix: '/dashboard/teacher', title: 'Lehrer Dashboard' },
      { prefix: '/dashboard/parent', title: 'Elternportal' },
      { prefix: '/public/calendar', title: 'Öffentlicher Kalender' },
      { prefix: '/auth/login', title: 'Login' },
      { prefix: '/auth/reset-password', title: 'Passwort zurücksetzen' },
      { prefix: '/impressum', title: 'Impressum' },
      { prefix: '/datenschutz', title: 'Datenschutz' },
    ]

    const matched = routeTitles.find((entry) => path.startsWith(entry.prefix))
    const pageTitle = matched ? matched.title : 'FlexWise'
    document.title = pageTitle === 'FlexWise' ? pageTitle : `${pageTitle} | FlexWise`
  }, [location.pathname])

  return null
}

function App() {
  // Kiosk APK Redirection Logic
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const token = window.localStorage.getItem('fw_kiosk_jwt');
      const currentPath = window.location.pathname;

      // Only redirect if we are at root or not already in kiosk routes
      if (!currentPath.startsWith('/kiosk')) {
        if (token) {
          window.location.replace('/kiosk/display/substitution');
        } else {
          window.location.replace('/kiosk/setup');
        }
      }
    }
  }, []);

  // Use AuthContext for all authentication state
  const { user, session, loading, profileId, schoolId, userProfile, needsMfa, authError, refreshProfile } = useAuth()

  // Once the user has a session and profile, never tear down the whole <Router> for a
  // transient `loading` blip (e.g. token refresh / profile refetch). That used to wipe
  // open dialogs when switching browser tabs. Reset when signed out.
  const authedShellEstablishedRef = useRef(false)
  useEffect(() => {
    if (!session) {
      authedShellEstablishedRef.current = false
      return
    }
    if (userProfile) {
      authedShellEstablishedRef.current = true
    }
  }, [session, userProfile])

  // Local UI state only

  // Memoize dashboard path to prevent render loops
  const dashboardPath = useMemo(() => {
    // When `needsMfa` is true the user has factors enrolled but is still
    // at AAL1. AuthContext intentionally skips the profile fetch in that
    // state so there's no `userProfile.role` to route by. Keep them at
    // root and let MFAGuard render the challenge — once they reach AAL2
    // the profile loads and we redirect to the proper dashboard.
    if (needsMfa) return '/'
    if (!userProfile || !userProfile.role) {

      return DEFAULT_ROUTE
    }
    const path = ROLE_ROUTES[userProfile.role] || DEFAULT_ROUTE

    return path
  }, [userProfile?.role, needsMfa])

  // Strict protected route - clear error handling, no fallbacks
  const ProtectedRoute = ({ children }) => {
    if (authError) {
      console.error('❌ ProtectedRoute auth error:', authError)
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="text-red-600 text-lg font-semibold mb-2">Authentication Failed</div>
              <div className="text-red-700 mb-4">{authError}</div>
              <button onClick={() => supabase.auth.signOut({ scope: 'global' })} className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">Return to Login</button>
            </div>
          </div>
        </div>
      )
    }
    if (loading || (user && !userProfile)) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-lg">Loading…</div>
        </div>
      )
    }
    if (!user) {
      return <RedirectWithLog to="/auth/login" reason="no session in ProtectedRoute" />
    }
    if (!userProfile) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center text-sm text-muted-foreground">Profil wird geladen…</div>
        </div>
      )
    }
    return children
  }

  // Admin-only route guard
  const AdminRoute = ({ children }) => {
    const allowed = userProfile?.role === 'Admin' || userProfile?.role === 'Super Admin'
    if (!allowed) {
      // Redirect non-admins to their role dashboard
      return <Navigate to={dashboardPath.replace(/^\/dashboard\/admin$/, '/dashboard')} replace />
    }
    return <ProtectedRoute>{children}</ProtectedRoute>
  }

  // Super-Admin-only route guard
  const SuperAdminRoute = ({ children }) => {
    const allowed = userProfile?.role === 'Super Admin'
    if (!allowed) {
      return <Navigate to={dashboardPath.replace(/^\/dashboard\/admin$/, '/dashboard')} replace />
    }
    return <ProtectedRoute>{children}</ProtectedRoute>
  }

  // Staff-only route guard (prevents parents/students from accessing teacher dashboard)
  const StaffRoute = ({ children }) => {
    const allowed = userProfile?.role === 'Teacher' || userProfile?.role === 'Erzieher*innen' || userProfile?.role === 'Admin' || userProfile?.role === 'Super Admin' || userProfile?.role === 'Externe'
    if (!allowed) {
      return <Navigate to={dashboardPath} replace />
    }
    return <ProtectedRoute>{children}</ProtectedRoute>
  }

  // Dashboard renderer that shows the correct component based on user role
  const DashboardRenderer = ({ user, profile }) => {
    if (!profile) {
      return <Navigate to="/auth/login" replace />
    }


    const dashboardProps = { user, profile }

    // Render appropriate dashboard based on role (no internal redirects)
    switch (profile.role) {
      case 'Admin':
      case 'Super Admin':
        return <AdminDashboard {...dashboardProps} />
      case 'Teacher':
      case 'Erzieher*innen':
        return <TeacherDashboard {...dashboardProps} />
      case 'Parent':
      case 'Student':
        return <ParentPortalV2 {...dashboardProps} />
      case 'Externe':
        return <TeacherDashboard {...dashboardProps} />
      default:
        console.error('Unknown role:', profile.role)
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">Your account role ({profile.role}) is not recognized.</p>
              <button
                onClick={() => supabase.auth.signOut({ scope: 'global' })}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Sign Out and Try Again
              </button>
            </div>
          </div>
        )
    }
  }


  // Helper: build demo session from localStorage
  const getDemoSession = () => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('fw_demo_session') : null
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.user) return null
      return { user: parsed.user, is_demo: true, role: parsed.role || 'Teacher' }
    } catch {
      return null
    }
  }

  function AuthedRoutes() {
    const location = useLocation()
    const navigate = useNavigate()
    const state = location.state

    // Preserve intended path (deeplink) across MFA
    const intendedPath = useMemo(() => {
      const raw = state?.backgroundLocation?.pathname || location.pathname || '/'
      // Avoid auth pages
      const safe = (/^\/auth\//.test(raw) || raw === '/login') ? dashboardPath : raw
      return safe
    }, [state, location.pathname, dashboardPath])

    return (
      <>
        {/* Allow password reset even when authenticated, without MFA gate.
            Catch-all required: this Routes shares the same location as MFAGuard's tree; without it,
            RR warns "No routes matched" for paths like /dashboard/klassenuebersicht/liste. */}
        <Routes location={state?.backgroundLocation || location}>
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/public/calendar" element={
            <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
              <PublicCalendarRoute />
            </Suspense>
          } />
          <Route path="/public/flexmodule-schaufenster" element={
            <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
              <PublicShowcaseRoute />
            </Suspense>
          } />
          {/* Legacy compatibility — see comment in the upper Routes block. */}
          <Route path="/public/flexmodule-schaufenster/:legacyToken" element={<LegacyTokenRedirect to="/public/flexmodule-schaufenster" />} />
          {/* Kiosk routes mirror the UnauthedRoutes entries so an authenticated
              admin can also dev-test them without being bounced to the dashboard. */}
          <Route path="/kiosk/chip-test" element={<KioskChipTestPage />} />
          <Route path="/kiosk/klassenbuch" element={<KioskKlassenbuchPage />} />
          <Route path="*" element={null} />
        </Routes>
        <MFAGuard
          user={session?.user}
          onMFAComplete={async () => {
            // supabase-js's `mfa.verify` writes the new aal=aal2 token to
            // localStorage but does NOT reliably emit a TOKEN_REFRESHED
            // event that AuthContext's `onAuthStateChange` listener picks
            // up. Without that event, AuthContext's `session` state is
            // stuck on the aal1 token, `needsMfa` stays true,
            // `profileQuery` stays skipped, `dashboardPath` stays `/`,
            // and the inner Route's `<Navigate to={dashboardPath}>`
            // bounces to `/` → white screen.
            //
            // Forcing `refreshSession()` here makes the AAL2 token the
            // active session AND fires TOKEN_REFRESHED, which threads the
            // AAL change all the way through AuthContext → needsMfa →
            // profileQuery → dashboardPath in one cascade.
            try { await supabase.auth.refreshSession() } catch { }
            try { await refreshProfile() } catch { }
            try { navigate('/', { replace: true }) } catch { }
          }}
        >
          <Routes location={state?.backgroundLocation || location}>
            {/* Keep reset-password route inside to avoid wildcard redirect */}
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={<Navigate to={dashboardPath} replace />} />
            {/* Explicit routes to preserve state across refreshes */}
            <Route path="/dashboard/flexmodule-vorschlaege" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <FlexModuleProposalRoutePage />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/dashboard/newsletter" element={
              <SuperAdminRoute>
                <Suspense fallback={null}>
                  <NewsletterRoutePage />
                </Suspense>
              </SuperAdminRoute>
            } />
            <Route path="/dashboard/newsletter/meine-fotos" element={
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <NewsletterTeacherRoutePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/newsletter/vorschau/:editionId" element={
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <NewsletterSchoolPreviewRoute />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/admin/kursuebersicht" element={
              <AdminRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <AdminDashboard user={session?.user} profile={userProfile} initialView="kurs-ubersicht" />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/dashboard/teacher" element={
              <StaffRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_teacher" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <TeacherDashboard user={session?.user} profile={userProfile} />
                  </Suspense>
                </ChunkGuardBoundary>
              </StaffRoute>
            } />
            <Route path="/dashboard/admin" element={
              <AdminRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_admin" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <AdminDashboard user={session?.user} profile={userProfile} />
                  </Suspense>
                </ChunkGuardBoundary>
              </AdminRoute>
            } />
            {/* Fehlzeiten routes with URL-based detail view */}
            <Route path="/dashboard/admin/fehlzeiten" element={
              <StaffRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_fehlzeiten" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <FehlzeitenLayout />
                  </Suspense>
                </ChunkGuardBoundary>
              </StaffRoute>
            }>
              <Route index element={null} />
              <Route path=":fehlzeitId" element={
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <FehlzeitDetailRoute />
                </Suspense>
              } />
            </Route>
            <Route path="/dashboard/parent" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <ParentPortalV2 user={session?.user} profile={userProfile} />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/debug/rls-inspector" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <RlsInspectorPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/kiosk/pair" element={
              <ProtectedRoute>
                <KioskPairPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/parent-v2" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <ParentPortalV2 user={session?.user} profile={userProfile} />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/external" element={
              <ProtectedRoute>
                <Navigate to="/dashboard/teacher" replace />
              </ProtectedRoute>
            } />
            <Route path="/dashboard/vertretungsplanung" element={
              <ProtectedRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_vertretung" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <VertretungsplanungDashboard />
                  </Suspense>
                </ChunkGuardBoundary>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/vertretungsplanung/layout" element={
              <ProtectedRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_vertretung_layout" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <VertretungsplanungLayoutPage />
                  </Suspense>
                </ChunkGuardBoundary>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/vertretungsplanung/uebersicht" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <PlanningOverviewPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/vertretungsplanung/statistik" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <StatisticsPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/kurse/anwesenheitsliste" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                  <CourseAttendanceReportPage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/kalender" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <CalendarRouteWrapper />
                </Suspense>
              </StaffRoute>
            } />
            {/* StaffRoute intentional: teachers see read-only view (readOnly prop), admin writes enforced by backend RPCs */}
            <Route path="/dashboard/aufsichten" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <AufsichtenPage />
                </Suspense>
              </StaffRoute>
            } />
            <Route path="/dashboard/flex-uebersicht" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <FlexbandRoutePage />
                </Suspense>
              </StaffRoute>
            } />
            <Route path="/dashboard/wuensche" element={
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <WuenscheRoutePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/klassenbuch" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <KlassenbuchRoutePage />
                </Suspense>
              </StaffRoute>
            } />
            <Route path="/dashboard/hilfe/*" element={
              <ProtectedRoute>
                <Suspense fallback={null}>
                  <HilfeRoutePage />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/pruefungen" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <PruefungenRoutePage />
                </Suspense>
              </StaffRoute>
            } />
            {/* Klassenübersicht — URL-based tabs */}
            <Route path="/dashboard/klassenuebersicht/*" element={
              <StaffRoute>
                <Suspense fallback={null}>
                  <KlassenuebersichtRoutePage />
                </Suspense>
              </StaffRoute>
            } />
            <Route path="/dashboard/berichte/baende-statistik" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <BaendeStatisticsRoutePage />
                </Suspense>
              </AdminRoute>
            } />
            {/* Bänder-Statistik (Berichte) */}
            <Route path="/dashboard/berichte/baende-statistik" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <BaendeStatisticsRoutePage />
                </Suspense>
              </AdminRoute>
            } />
            {/* Stundenplan-Planung (Stecken) */}
            <Route path="/dashboard/stundenplan-planung" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <StundenplanPlanungPage />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/dashboard/stundenplan-planung/verteilung" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <VerteilungRoutePage />
                </Suspense>
              </AdminRoute>
            } />
            {/* Teacher Umfrage (staff yearly planning survey) */}
            <Route path="/dashboard/umfrage" element={
              <Suspense fallback={null}>
                <TeacherUmfrageRoutePage />
              </Suspense>
            } />
            <Route path="/dashboard/admin/umfrage" element={
              <AdminRoute>
                <Suspense fallback={null}>
                  <AdminUmfrageRoutePage />
                </Suspense>
              </AdminRoute>
            } />
            {/* Einstellungen (Settings) */}
            <Route path="/dashboard/einstellungen/*" element={
              <AdminRoute>
                <ChunkGuardBoundary storageKey="__chunk_guard_einstellungen" fallback={<LoadingScreen onComplete={() => { }} />}>
                  <Suspense fallback={<LoadingScreen onComplete={() => { }} />}>
                    <SettingsRoutePage />
                  </Suspense>
                </ChunkGuardBoundary>
              </AdminRoute>
            } />
            {/* Compat redirect for old bookmarks/stale clients */}
            <Route path="/dashboard/planung" element={
              <Navigate to="/dashboard/stundenplan-planung" replace />
            } />
            <Route path="/dashboard/kursuebersicht" element={
              <Navigate to="/dashboard/admin/kursuebersicht" replace />
            } />
            <Route path="/error/missing-role" element={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="text-yellow-700 text-lg font-semibold mb-2">Role information not available</div>
                    <div className="text-yellow-800 mb-4">Please complete MFA to load your role, then try again.</div>
                    <button onClick={() => supabase.auth.signOut({ scope: 'global' })} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                      Return to Login
                    </button>
                  </div>
                </div>
              </div>
            } />
            {/* Fallback to role-based renderer for other /dashboard/* paths */}
            <Route path="/dashboard/*" element={
              <ProtectedRoute>
                <DashboardRenderer user={session?.user} profile={userProfile} />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to={dashboardPath} replace />} />
            {/* Kiosk pages render in the upper pre-MFA Routes block above;
                stop the wildcard below from redirecting them to the dashboard. */}
            <Route path="/kiosk/chip-test" element={null} />
            <Route path="/kiosk/klassenbuch" element={null} />
            <Route path="*" element={<Navigate to={dashboardPath} replace />} />
          </Routes>
        </MFAGuard>
      </>
    )
  }
  // (Removed) validateSession effect: it duplicated a server round-trip on
  // every boot (`supabase.auth.getUser()`) to check if the token was
  // revoked. The first real RPC call (`getMyProfileAndRoles`) already
  // fails with 401 if the token is dead, and the AuthContext sign-out
  // handler redirects from there. Kept the comment so the next person
  // asking "where's the safety check?" finds this note instead of
  // re-adding it.

  // Show loading state from AuthContext (initial boot only — not after authed UI mounted)
  if (loading && !authedShellEstablishedRef.current) {
    if (shouldShowLoadingScreen()) {
      return <LoadingScreen onComplete={() => { }} />
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  // Main render
  return (
    <DebugProvider>
      <ConnectivityProvider>
        <TooltipProvider delayDuration={0}>
          <Router>
            <RouteDocumentTitle />
            <OfflineBanner />
            {/* Show SW update prompt when a new version is ready */}
            <UpdateBanner />
            <div className="min-h-screen bg-gray-100 relative pt-0 flex flex-col">
              {!session ? (
                <UnauthedRoutes />
              ) : (
                <SchoolRealtimeProvider>
                  <AuthedRoutes />
                </SchoolRealtimeProvider>
              )}
              <CookieBanner />
              {typeof APP_VERSION !== 'undefined' && (
                <div className="mt-auto w-full px-3 py-2 text-xs text-gray-400 select-none">
                  v{APP_VERSION}{GIT_COMMIT ? ` (${GIT_COMMIT})` : ''}
                </div>
              )}
            </div>
            <DebugModal />
            <GlobalLegalModals />
            {/* Global Auth Debug overlay (debug mode only) */}
            <AuthDebugGate />
            <Toaster position="top-right" richColors />
          </Router>
        </TooltipProvider>
      </ConnectivityProvider>
    </DebugProvider>
  )
}



export default App
